import { Seo } from "@/components/Seo";
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { AppLink } from '@/components/AppLink';

const FAQ = () => {
  return (
    <div className='min-h-screen'>
      <Seo
        title="FAQ — Perch Mobile"
        description="Answers to common questions about Perch Mobile wireless plans, credit building, coverage, billing, and account management."
        path="/faq"
      />
      <Navigation />
      <main className='pt-24 pb-16'>
        <div className='container mx-auto px-4'>
          <div className='max-w-3xl mx-auto'>
            <h1 className='text-4xl md:text-5xl font-bold mb-4 text-foreground'>
              Frequently Asked Questions
            </h1>
            <p className='text-lg text-muted-foreground mb-12'>
              Find answers to common questions about Perch Mobile
            </p>

            {/* About Perch Mobile */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                About Perch Mobile
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='about-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What is Perch Mobile?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Perch Mobile is a new type of mobile company, one that
                      puts the control in your hands. Perch was founded on a
                      simple idea: what if your monthly phone bill actually
                      invested in your future? What if the service you pay for
                      every month helped you build credit, what if you paid for
                      only the data you actually used, and you were in complete
                      control?
                    </p>
                    <p>
                      That's why we built the Perch mobile service differently.
                    </p>
                    <p>
                      We run on AT&T's trusted 5G network with plans starting at
                      $7.99/month. If you opt in to our FREE credit building
                      partner program, every on-time payment builds your credit
                      history. And you're never locked in, change your plan or
                      cancel anytime.
                    </p>
                    <p>
                      We're built for self investors who care about where their
                      money goes, and we're here to invest in you.
                    </p>
                    <p>
                      Mobile service designed around your life, built for your
                      future.
                    </p>
                    <p>Your money. Your data. Your future.</p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='about-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What network does Perch use?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    5G/4G LTE speeds on AT&T's trusted network. Same towers.
                    Real pricing.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='about-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What services do you offer?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Month-to-month wireless plans with unlimited talk and text,
                    plus data options from 1GB to Unlimited. We also offer plans
                    for your wearables. Check out all our plans at{' '}
                    <Link to='/plans' className='text-accent hover:underline'>
                      our plans page
                    </Link>
                    .
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Making the Switch */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Making the Switch
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='switch-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How do I switch to Perch?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Head to our{' '}
                      <Link to='/plans' className='text-accent hover:underline'>
                        plans page
                      </Link>{' '}
                      to choose the plan that fits your life.
                    </p>
                    <p>
                      Once you select the right plan for you, simply click
                      "Start Building" and we will take you to a basic summary
                      of the plan you chose.
                    </p>
                    <p>
                      If everything looks good, continue to checkout and
                      purchase your plan.
                    </p>
                    <p>
                      Once completed, you receive instructions on how to access
                      your Perch account and complete the activation process.
                      It's all very straightforward!
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='switch-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Can I keep my current phone number?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Yes! This is called porting, and it lets you transfer your
                      number to Perch. Your current service typically stays
                      active until the switch completes, though a short
                      interruption may occur during the transfer.
                    </p>
                    <p>To ensure a smooth switch, make sure:</p>
                    <ul className='list-disc pl-6 space-y-1'>
                      <li>Your phone is unlocked</li>
                      <li>Your number is still active</li>
                      <li>You have your account number and transfer PIN</li>
                    </ul>
                    <p>
                      <AppLink
                        to='/activation-guide'
                        className='text-accent hover:underline'
                      >
                        View Porting Instructions →
                      </AppLink>
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='switch-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Do I need a new phone?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    We doubt you do. Almost all phones work with Perch as long
                    as they're unlocked.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='switch-4'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Will I lose my contacts, photos, and text messages?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Nope. Back up your phone before switching to be safe, but
                      all on your phone should stay the same when you transfer
                      over. Your contacts, photos, and texts stay right where
                      they are.
                    </p>
                    <p>
                      <strong>Note:</strong> Voicemails from your old carrier
                      won't automatically transfer, so save any important ones
                      before you switch.
                    </p>
                    <p>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>
                      .
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='switch-5'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Does my phone need to be unlocked to switch to Perch?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Your phone needs to be unlocked to switch to Perch. Pick
                      your phone type below for instructions for checking if
                      your phone is locked or unlocked.
                    </p>

                    <div className='mt-4 p-4 bg-muted/30 rounded-lg'>
                      <h4 className='font-semibold text-foreground mb-2'>
                        Check if my iPhone is Unlocked:
                      </h4>
                      <ol className='list-decimal list-inside space-y-1'>
                        <li>Go to Settings</li>
                        <li>Tap General</li>
                        <li>Tap About</li>
                        <li>Scroll down to "Carrier Lock"</li>
                        <li>
                          If it says "No SIM Restrictions," you're unlocked and
                          ready to switch
                        </li>
                      </ol>
                    </div>

                    <div className='mt-4 p-4 bg-muted/30 rounded-lg'>
                      <h4 className='font-semibold text-foreground mb-2'>
                        Check if my Android is Unlocked:
                      </h4>
                      <p>
                        There's no one official way to check if your Android is
                        unlocked. The easiest way is to contact your current
                        carrier and ask them directly if your phone is unlocked.
                      </p>
                    </div>

                    <div className='mt-4 p-4 bg-muted/30 rounded-lg'>
                      <h4 className='font-semibold text-foreground mb-2'>
                        If your phone is locked:
                      </h4>
                      <p>
                        Contact your current carrier. They're the only ones who
                        can unlock it. Once they unlock your phone, you're free
                        to switch to Perch.
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Porting Your Number */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Porting Your Number
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='port-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What does porting mean?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Porting means keeping your current phone number when you
                    switch to Perch. You're transferring your number from your
                    old carrier to us.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What information do I need to port my number?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>You'll need:</p>
                    <ul className='list-disc list-outside pl-5 space-y-2'>
                      <li>
                        Your account number (found on your bill or in your
                        carrier's app)
                      </li>
                      <li>
                        Your transfer PIN from your current carrier. This is
                        usually a 4–6 digit number provided when you request a
                        "transfer out" from your existing carrier. All mobile
                        carriers are required to give you this number upon
                        request, so it's easy to obtain.
                      </li>
                      <li>The phone number you want to keep</li>
                      <li>
                        The billing ZIP code or address associated with your
                        current carrier account (used by your current carrier to
                        verify the transfer request)
                      </li>
                    </ul>
                    <p>
                      <strong>Important:</strong>
                    </p>
                    <ul className='list-disc list-outside pl-5 space-y-2'>
                      <li>
                        Make sure the number you're transferring is still active
                        with your current carrier when you submit the request.
                      </li>
                      <li>
                        Do not cancel your service with your current carrier.
                        Your service will automatically disconnect once your
                        number successfully transfers.
                      </li>
                    </ul>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Should I cancel my service with my old carrier before
                    porting?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>No!</strong> Don't cancel your service with your
                      old carrier. It automatically disconnects once your number
                      transfers over to Perch. If you cancel early, you could
                      lose your number.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                {/* Carrier-specific porting instructions */}
                <AccordionItem
                  value='port-att'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from AT&T
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Number Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <p>
                      Find it at the top of your bill, or in the myAT&T app.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Number Transfer PIN
                    </h4>
                    <p>Choose one of these methods:</p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the myAT&T app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Tap your profile icon</li>
                        <li>Tap Settings</li>
                        <li>Scroll down to "Transfer phone number"</li>
                        <li>Select "Request a new PIN"</li>
                        <li>Your PIN will be sent via text</li>
                      </ol>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>
                        Or dial from your AT&T phone:
                      </p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Dial *7678</li>
                        <li>Follow the prompts</li>
                        <li>Your PIN will be sent via text</li>
                      </ul>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your AT&T service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with AT&T. Your
                      service will automatically disconnect once your number
                      transfers over to Perch. If you cancel early, you could
                      lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact AT&T if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: AT&T Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-verizon'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Verizon
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Number Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>On your bill:</p>
                      <p className='mt-1'>
                        Look at the top right corner of your Verizon bill
                      </p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Verizon app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Go to Accounts</li>
                        <li>Tap Account Settings</li>
                        <li>Your account number is at the top of the screen</li>
                      </ol>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Number Transfer PIN
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Verizon app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Stay in Account Settings</li>
                        <li>Scroll down until you see "Transfer PIN"</li>
                        <li>Generate your PIN there</li>
                      </ol>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your Verizon service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Verizon.
                      Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Verizon if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Verizon Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-tmobile'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from T-Mobile
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your 9-digit account number</li>
                      <li>Your 6-digit Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>On your bill:</p>
                      <p className='mt-1'>
                        Check your T-Mobile bill for your 9-digit account number
                      </p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the T-Life app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Click Manage at the bottom right</li>
                        <li>
                          Your account number is at the top under your name
                        </li>
                      </ol>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Transfer PIN
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the T-Life app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>
                          From the Manage page, click the Settings gear at the
                          top right
                        </li>
                        <li>Go to Permissions & Controls</li>
                        <li>
                          Scroll to the bottom and you'll see "Transfer PIN"
                        </li>
                        <li>Your 6-digit PIN is there</li>
                      </ol>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your T-Mobile service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with T-Mobile.
                      Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact T-Mobile if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: T-Mobile Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-googlefi'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Google Fi
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Get your account number and Transfer PIN
                    </h4>
                    <p>
                      Google Fi makes this easy—you get both at the same time.
                    </p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>
                        In the Google Fi app or website:
                      </p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Sign in to your account</li>
                        <li>Go to the Account tab</li>
                        <li>Select "Manage Plan"</li>
                        <li>Scroll down and select "Leave Google Fi"</li>
                        <li>Follow the on-screen instructions</li>
                        <li>
                          Select "Transfer your number to another carrier"
                        </li>
                        <li>
                          After you confirm, a "Cancellation pending" banner
                          will appear
                        </li>
                        <li>Under the banner, select "Get Transfer Info"</li>
                        <li>
                          You'll see both your account number and Transfer PIN
                        </li>
                      </ol>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Don't cancel anything manually
                    </h4>
                    <p>
                      This is important: Google Fi will automatically close your
                      account once the port is complete. Don't cancel your
                      service manually or you could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Google Fi if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Google Fi Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-cricket'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Cricket Wireless
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <p>
                      Find it on your billing statement or in the myCricket app.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Transfer PIN
                    </h4>
                    <p>Choose one of these methods:</p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Request PIN by text:</p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Dial *7678 from your Cricket phone</li>
                        <li>Your PIN will be sent via text</li>
                      </ul>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Call customer service:</p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Dial *611 from your Cricket phone</li>
                        <li>Ask them for your Transfer PIN</li>
                      </ul>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your Cricket service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Cricket.
                      Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Cricket if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Cricket Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-visible'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Visible
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Visible app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Go to Account</li>
                        <li>Click Profile</li>
                        <li>Your account number is there</li>
                      </ol>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>On your bill:</p>
                      <p className='mt-1'>Look at the top of your last bill</p>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Transfer PIN
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Visible app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Open the app and go to the Account section</li>
                        <li>Tap "Port-Out PIN"</li>
                        <li>Click to request via email</li>
                        <li>They'll send you your PIN</li>
                      </ol>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Or call:</p>
                      <p className='mt-1'>
                        Call 833-488-4782 — they can help you with both your
                        account number and PIN
                      </p>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your Visible service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Visible.
                      Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Visible if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Visible Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-mint'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Mint Mobile
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Number Transfer PIN</li>
                    </ul>

                    <div className='p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg'>
                      <p className='font-semibold text-foreground'>
                        Important: Check your balance first
                      </p>
                      <p className='mt-1'>
                        Before you start, make sure you don't have any
                        outstanding balances with Mint. They might block your
                        port request if you owe them money.
                      </p>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 1: Contact Mint support to get your info
                    </h4>
                    <p>
                      You'll need to contact Mint to get both your account
                      number and Transfer PIN.
                    </p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Mint app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Open the Mint Mobile app</li>
                        <li>Go to chat support</li>
                        <li>Type "To Port Out"</li>
                        <li>They'll provide your account number and PIN</li>
                      </ol>
                    </div>
                    <p className='mt-2'>
                      Or call Mint customer support if you prefer talking to
                      someone.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Don't cancel your Mint service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Mint. Your
                      service will automatically disconnect once your number
                      transfers over to Perch. If you cancel early, you could
                      lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Mint if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Mint Mobile Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-boost'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Boost Mobile
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the My Boost Mobile app:</p>
                      <p className='mt-1'>
                        Check the app for your account number
                      </p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Online:</p>
                      <p className='mt-1'>
                        Log into your Boost Mobile account online
                      </p>
                    </div>
                    <p className='mt-2'>
                      Or call customer service and ask for your account number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Transfer PIN
                    </h4>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the My Boost Mobile app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Open the app</li>
                        <li>Navigate to the Account section</li>
                        <li>
                          Select the option related to porting or number
                          transfer
                        </li>
                        <li>Request your Number Transfer PIN there</li>
                      </ol>
                    </div>
                    <p className='mt-2'>
                      Or call Boost customer service and ask for your Transfer
                      PIN.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your Boost service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Boost. Your
                      service will automatically disconnect once your number
                      transfers over to Perch. If you cancel early, you could
                      lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Boost if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Boost Mobile Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-straighttalk'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from Straight Talk
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <p>Find it in the Account tab on the Straight Talk app.</p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Transfer PIN
                    </h4>
                    <p>Choose one of these methods:</p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>In the Straight Talk app:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Navigate to Account and Payment Info</li>
                        <li>Select "Request Transfer PIN"</li>
                        <li>Your PIN will be provided</li>
                      </ol>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Chat with support:</p>
                      <p className='mt-1'>
                        Use the chat feature in the Straight Talk app or online
                      </p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Or call:</p>
                      <p className='mt-1'>
                        Dial *611 from your Straight Talk phone to talk with an
                        agent
                      </p>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your Straight Talk service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with Straight
                      Talk. Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact Straight Talk if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: Straight Talk Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='port-cspire'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Porting from C Spire
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>What you need:</strong>
                    </p>
                    <ul className='list-disc list-inside mb-4'>
                      <li>Your account number</li>
                      <li>Your Number Transfer PIN</li>
                    </ul>

                    <h4 className='font-semibold text-foreground'>
                      Step 1: Find your account number
                    </h4>
                    <p>Find it on your C Spire bill or in your phone app.</p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 2: Get your Number Transfer PIN
                    </h4>
                    <p>Choose one of these methods:</p>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Open a support chat online:</p>
                      <p className='mt-1'>
                        Go to the C Spire website and start a chat with support
                      </p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Send a text:</p>
                      <p className='mt-1'>Text 611 from your C Spire phone</p>
                    </div>
                    <div className='p-4 bg-muted/30 rounded-lg mt-2'>
                      <p className='font-medium'>Or call:</p>
                      <p className='mt-1'>Call 1-855-277-4735</p>
                    </div>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 3: Don't cancel your C Spire service
                    </h4>
                    <p>
                      This is important: Don't cancel anything with C Spire.
                      Your service will automatically disconnect once your
                      number transfers over to Perch. If you cancel early, you
                      could lose your number.
                    </p>

                    <h4 className='font-semibold text-foreground mt-4'>
                      Step 4: Make sure your phone is unlocked
                    </h4>
                    <p>
                      Your phone needs to be unlocked to complete the transfer.
                      See "Does my phone need to be unlocked?" above to check,
                      or contact C Spire if you need to unlock it.
                    </p>

                    <p className='mt-4'>
                      Need help? Email us at{' '}
                      <a
                        href='mailto:support@perchmobile.com'
                        className='text-accent hover:underline'
                      >
                        support@perchmobile.com
                      </a>{' '}
                      and we'll walk you through it.
                    </p>

                    {/* Video Placeholder */}
                    <div className='mt-4 p-8 bg-muted/30 rounded-lg border-2 border-dashed border-border text-center'>
                      <p className='text-muted-foreground'>
                        [ Video: C Spire Porting Guide Coming Soon ]
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Credit Building */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Credit Building
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='credit-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How does Credit Building work?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Perch offers an optional Credit Building program for
                      eligible customers.
                    </p>
                    <p>
                      Customers who opt in begin working toward eligibility by
                      making 6 consecutive on-time monthly payments on their
                      Perch Mobile service.
                    </p>
                    <p>
                      Once you qualify, we'll provide additional information and
                      instructions for participating in the program.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='credit-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    When does Credit Building start?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Your qualification period begins when your eligible Perch
                      service becomes active and you begin making consecutive
                      on-time monthly payments.
                    </p>
                    <p>
                      After 6 consecutive qualifying payments, we'll notify you
                      if you're eligible for the next step.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='credit-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Does Perch run a credit check?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>No.</p>
                    <p>
                      Opting in to the Credit Building program does not require
                      a credit check.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='credit-4'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What credit bureaus does this report to?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Perch is currently updating its Credit Building program.
                    </p>
                    <p>
                      Additional information about reporting and participating
                      partners will be provided to eligible customers once they
                      qualify.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Plans & Pricing */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Plans & Pricing
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='plans-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What plans do you offer?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Plans starting at $7.99/month with options from 1GB to
                    Unlimited. All plans include unlimited talk and text, and
                    credit building. Visit{' '}
                    <Link to='/plans' className='text-accent hover:underline'>
                      our plans page
                    </Link>{' '}
                    to see all options.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='plans-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Can I change my plan?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Yes. Month-to-month freedom means you can change your plan
                    anytime. Need more data this month? Less next month? Change
                    it. Simple as that.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='plans-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Can I cancel anytime?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Yes. Cancel anytime. You're in control. No long-term
                    commitment, just mobile service that works for you.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='plans-4'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How much data do I really need?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Choosing the right plan mostly depends on how often you're
                      away from WiFi. Here's a quick guide:
                    </p>
                    <p>
                      <strong>5GB</strong>
                    </p>
                    <p>
                      Great if you're usually on WiFi at home, work, or school
                      but want data for maps, messaging, checking social media,
                      and light browsing while you're out.
                    </p>
                    <p>
                      <strong>Unlimited</strong>
                    </p>
                    <p>
                      Best if you stream music or video, scroll social feeds
                      often, or just don't want to think about how much data
                      you're using.
                    </p>
                    <p>
                      <strong>Wearable (1GB)</strong>
                    </p>
                    <p>
                      Designed for smartwatches or tablets that need occasional
                      connectivity when you're away from your phone or WiFi.
                    </p>
                    <p>
                      The good news? You can always change your plan later if
                      your usage changes.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Billing & Payments */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Billing & Payments
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='billing-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How does billing work?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Perch plans are paid in advance, and you can choose
                      monthly or annual billing when you sign up.
                    </p>
                    <p>
                      <strong>Monthly billing:</strong> Your plan renews
                      automatically each month.
                    </p>
                    <p>
                      <strong>Annual billing:</strong> Pay for the full year
                      upfront and save with a discounted rate.
                    </p>
                    <p>
                      You can view your billing details, payment history, and
                      update your payment method anytime from your account.
                    </p>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='billing-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    What payment methods do you accept?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Credit cards, debit cards, and ACH payments. Autopay is
                    required for all Perch Mobile plans.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='billing-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Where can I view my bill?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    Log into your{' '}
                    <AppLink to='/login' className='text-accent hover:underline'>
                      Perch Dashboard
                    </AppLink>{' '}
                    to view your bill anytime.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Technical Support */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Technical Support
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='tech-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How do I find my IMEI?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    The easiest way works on any phone: Open your phone app and
                    dial <strong>*#06#</strong>. Your IMEI appears instantly.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='tech-2'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How do I find my IMEI on my iPhone?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>
                        Fastest method - Works on any iPhone:
                      </p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Open your Phone app</li>
                        <li>Dial *#06#</li>
                        <li>Your IMEI appears on screen</li>
                      </ul>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>In Settings:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Go to Settings</li>
                        <li>Tap General</li>
                        <li>Tap About</li>
                        <li>Scroll down to find your IMEI/MEID or ICCID</li>
                        <li>Touch and hold any number to copy it</li>
                      </ol>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>Using Finder or iTunes:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Connect your iPhone to your computer</li>
                        <li>Mac (Catalina or later): Open Finder</li>
                        <li>Mac (Mojave or earlier) or PC: Open iTunes</li>
                        <li>Locate your device</li>
                        <li>
                          Find your IMEI under General (Finder) or Summary
                          (iTunes)
                        </li>
                      </ol>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>
                        Don't have your device handy?
                      </p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Go to appleid.apple.com</li>
                        <li>Sign in with your Apple ID</li>
                        <li>Choose the Devices section</li>
                        <li>Select your device to see the IMEI</li>
                      </ol>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='tech-3'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How do I find my IMEI on my iPad?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      <strong>For iPad (Wi-Fi + Cellular models):</strong>
                    </p>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>Fastest method:</p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Open your Phone app (if available)</li>
                        <li>Dial *#06#</li>
                        <li>Your IMEI appears on screen</li>
                      </ul>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>On the device itself:</p>
                      <p className='mt-1'>
                        Look on the back of your iPad. The serial number and
                        IMEI are printed there.
                      </p>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>In Settings:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Go to Settings</li>
                        <li>Tap General</li>
                        <li>Tap About</li>
                        <li>Scroll down to find your IMEI</li>
                        <li>Touch and hold the number to copy it</li>
                      </ol>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>Using Finder or iTunes:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Connect your iPad to your computer</li>
                        <li>Mac (Catalina or later): Open Finder</li>
                        <li>Mac (Mojave or earlier) or PC: Open iTunes</li>
                        <li>Locate your device</li>
                        <li>Click Serial Number to find the IMEI</li>
                      </ol>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value='tech-4'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How do I find my IMEI on my Android?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>
                        Fastest method - Works on any Android:
                      </p>
                      <ul className='list-disc list-inside mt-2'>
                        <li>Open your Phone app</li>
                        <li>Dial *#06#</li>
                        <li>Your IMEI appears on screen</li>
                      </ul>
                    </div>

                    <div className='p-4 bg-muted/30 rounded-lg'>
                      <p className='font-medium'>Check IMEI from Settings:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Open Settings</li>
                        <li>Tap About phone</li>
                        <li>Scroll down to find your IMEI</li>
                      </ol>
                      <p className='mt-2'>OR:</p>
                      <ol className='list-decimal list-inside space-y-1 mt-2'>
                        <li>Open Settings</li>
                        <li>Tap About phone</li>
                        <li>Tap "Detailed info and specs"</li>
                        <li>Your IMEI is listed there</li>
                      </ol>
                    </div>

                    <p className='text-sm'>
                      <strong>Note:</strong> The exact steps may vary slightly
                      depending on your Android device manufacturer.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Referrals */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Referrals
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='referral-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    Do you have a referral program?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground space-y-4'>
                    <p>
                      Yes! Share Perch with friends and you both benefit. Visit
                      your{' '}
                      <AppLink to='/login' className='text-accent hover:underline'>
                        dashboard
                      </AppLink>{' '}
                      for more information on how it works and how to get your
                      referral link.
                    </p>
                    <p>
                      You may be compensated for referring people to Perch.
                      Please check your dashboard for details.
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Partnerships */}
            <section className='mb-12'>
              <h2 className='text-2xl font-bold mb-6 text-foreground'>
                Partnerships
              </h2>
              <Accordion type='single' collapsible className='space-y-4'>
                <AccordionItem
                  value='partner-1'
                  className='border border-border rounded-lg px-6'
                >
                  <AccordionTrigger className='text-left text-foreground hover:text-accent'>
                    How can my business partner with Perch?
                  </AccordionTrigger>
                  <AccordionContent className='text-muted-foreground'>
                    We're building something better, and we'd love to work with
                    companies that share our values. Email{' '}
                    <a
                      href='mailto:support@perchmobile.com'
                      className='text-accent hover:underline'
                    >
                      support@perchmobile.com
                    </a>{' '}
                    to start the conversation.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </section>

            {/* Still Have Questions */}
            <section className='mt-16 p-8 bg-muted/30 rounded-2xl border border-border text-center'>
              <h2 className='text-2xl font-bold mb-4 text-foreground'>
                Still Have Questions?
              </h2>
              <p className='text-muted-foreground mb-6'>
                If you have any questions, need assistance, or want to learn
                more about our services, our dedicated customer support team is
                here to help. Feel free to reach out to us via phone, email, or
                visit our support page for more information.
              </p>
              <div className='flex flex-col sm:flex-row gap-4 justify-center'>
                <Link
                  to='/contact'
                  className='inline-flex items-center justify-center px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent/90 transition-colors'
                >
                  Email Support
                </Link>
                <Link
                  to='/contact'
                  className='inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors'
                >
                  Contact Us
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default FAQ;
